
import { defineComponent, computed, ref, onBeforeMount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'

import InputGroup from '@/components/layout/InputGroup.vue'
import RequestHeaderLoginWrapper from '@/components/auth-wrappers/RequestHeaderLoginWrapper.vue'
import MessageAttacheFiles from '@/components/inputs/MessageAttacheFiles.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'

import { messageRule } from '@/constants/rules'
import { Message, RequestHeaderDetail } from '@/api'
import MessageTypeLogic from '@/services/messageTypeLogic'
import { MessageForm } from '@/types/forms'
import Logger from '@/services/logger'
import { ReadRequest as MessageReadRequest } from '@/requests/message'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import PushApplicantHistory from '@/components/actions/PushApplicantHistory.vue'
import ApplicantHistoryScheduleRadioGroup from '@/components/inputs/ApplicantHistoryScheduleRadioGroup.vue'
import { RequestTypes } from '@/constants/enums'

/**
 * 申請情報の取得
 */
const getRequestHeader = async (id: number): Promise<RequestHeaderDetail | null> => {
  const request = new RequestHeaderReadRequest(id)
  await request.exec()
  if (request.success) {
    const { data } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
    return data
  } else {
    return null
  }
}

export default defineComponent({
  name: 'ApplicantMessageHistoryDetail',
  components: {
    InputGroup,
    RequestHeaderLoginWrapper,
    MessageAttacheFiles,
    JrTextarea,
    InputLabel,
    PushApplicantHistory,
    ApplicantHistoryScheduleRadioGroup,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<InstanceType<typeof ElForm>>()
    const attacheFileRef = ref<InstanceType<typeof MessageAttacheFiles>>()

    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    const requestHeader = ref<RequestHeaderDetail | null>(null)
    // 種別
    const messageType = computed(() => {
      const type = Number(route.params.message_type)
      return Number.isNaN(type) ? null : type
    })
    // メッセージID
    const messageId = computed(() => {
      const id = Number(route.params.message_id)
      return Number.isNaN(id) ? null : id
    })
    // 種別ロジック
    const typeLogic = computed(() => {
      return messageType.value != null && requestHeaderId.value != null
        ? new MessageTypeLogic(messageType.value, requestHeaderId.value)
        : null
    })
    const isEdit = false
    // 添付ファイル可能か
    const canAttached = computed(() => {
      return typeLogic.value != null ? typeLogic.value.canAttachedFile() : false
    })
    // タイトル
    const title = computed(() => {
      if (typeLogic.value == null) {
        return ''
      }
      let typeName = typeLogic.value.getTypeName()
      return typeLogic.value.isJrSendType() ? `${typeName}のご連絡` : `${typeName}送信`
    })
    // 日程関連メッセージかどうか
    const isScheduleType = computed(() => {
      if (typeLogic.value == null) {
        return false
      }
      return typeLogic.value.isScheduleType()
    })
    // JRが送信できるメッセージかどうか
    const isJrSendType = computed(() => {
      if (typeLogic.value == null) {
        return false
      }
      return typeLogic.value.isJrSendType()
    })
    const form = ref<MessageForm | null>(null)
    const to = {
      name: 'ApplicantMessageHistory',
      params: {
        request_header_id: requestHeaderId.value,
      },
    }

    onBeforeMount(async () => {
      // 申請基本を取得する
      if (requestHeaderId.value == null) {
        Logger.info(`URLの申請基本IDが不正です ${route.params.request_header_id}`)
        router.push({ name: 'NotFound' })
        return
      } else {
        requestHeader.value = await getRequestHeader(requestHeaderId.value)
        if (requestHeader.value?.request_type !== RequestTypes.PROXIMITY) {
          // 近接工事では無ければ404へ飛ばす
          router.push({ name: 'NotFound' })
          return
        }
      }

      // メッセージを取得する
      if (messageId.value == null) {
        Logger.info('メッセージIDがありません')
        router.push({ name: 'NotFound' })
        return
      }
      const messageRequest = new MessageReadRequest(messageId.value)
      await messageRequest.exec()
      if (messageRequest.success) {
        const { data } = messageRequest.getResponse() as AxiosResponse<Message>
        if (messageType.value !== data.message_type) {
          Logger.info(
            `メッセージ種別が異なります。URLメッセージ種別: ${messageType.value} メッセージの種別: ${data.message_type}`
          )
          router.push({ name: 'NotFound' })
        }
        form.value = data as MessageForm
      }
    })

    return {
      requestHeaderId,
      title,
      formRef,
      form,
      attacheFileRef,
      isEdit,
      canAttached,
      rules: messageRule,
      to,
      messageId,
      isScheduleType,
      isJrSendType,
    }
  },
})
